import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";

const classes = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
    backgroundColor: theme.color.white,
    borderRadius: 12,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.backgroundText,
  },
  closeButton: {
    padding: 8,
  },
  selectedCount: {
    fontSize: 14,
    color: theme.color.primary,
    marginBottom: 16,
  },
  optionsList: {
    flex: 1,
    marginBottom: 20,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: theme.color.primary,
    borderRadius: 4,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: {
    backgroundColor: theme.color.primary,
  },
  checkIcon: {
    color: theme.color.white,
  },
  optionText: {
    flex: 1,
    fontSize: 14,
    color: theme.color.backgroundText,
  },
  optionTextSelected: {
    fontWeight: theme.font.weight.semibold,
    color: theme.color.primary,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: theme.color.lightGrey,
  },
  saveButton: {
    backgroundColor: theme.color.primary,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: theme.font.weight.semibold,
  },
  cancelButtonText: {
    color: theme.color.backgroundText,
  },
  saveButtonText: {
    color: theme.color.white,
  },
  optionsTextSelected: {
    textAlign: "center",
    fontStyle: "italic",
    padding: 20,
  },
});

export default function MultiSelectModal({
  visible,
  onClose,
  onSave,
  title,
  options = [],
  selectedIds = [],
  idField = "id",
  displayField = "name",
  showClearButton = false,
  onClear,
}) {
  const [searchText, setSearchText] = useState("");
  const [tempSelected, setTempSelected] = useState(selectedIds);

  useEffect(() => {
    if (visible) {
      setTempSelected(selectedIds || []);
      setSearchText("");
    }
  }, [visible, selectedIds, title, options.length, idField, displayField]);

  const filteredOptions = options.filter((option) => {
    const displayValue = option[displayField];
    return String(displayValue || "")
      ?.toLowerCase()
      .includes(searchText?.toLowerCase());
  });

  const handleToggleOption = useCallback((optionId) => {
    setTempSelected((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const newSelection = safePrev.includes(optionId)
        ? safePrev.filter((id) => id !== optionId)
        : [...safePrev, optionId];

      return newSelection;
    });
  }, []);

  const handleSave = useCallback(() => {
    onSave(tempSelected || []);
    onClose();
  }, [tempSelected, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setTempSelected(selectedIds);
    setSearchText("");
    onClose();
  }, [selectedIds, onClose]);

  const selectedCount = tempSelected?.length || 0;
  const totalCount = options?.length || 0;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={classes.overlay}>
        <View style={classes.modal}>
          <View style={classes.header}>
            <Text style={classes.title}>{title}</Text>
            <Pressable style={classes.closeButton} onPress={handleCancel}>
              <Icon name="close" size={24} color={theme.color.backgroundText} />
            </Pressable>
          </View>

          <Text style={classes.selectedCount}>
            {selectedCount} of {totalCount} selected
          </Text>
          <View>
            <SearchBar
              value={searchText}
              onChangeText={setSearchText}
              placeholder={`Search ${title?.toLowerCase()}...`}
            />
          </View>
          <ScrollView style={classes.optionsList}>
            {filteredOptions.length === 0 && searchText && (
              <Text style={[classes.optionText, classes.optionsTextSelected]}>
                No {title?.toLowerCase()} found matching "{searchText}"
              </Text>
            )}

            {filteredOptions.length === 0 && !searchText && (
              <Text style={[classes.optionText, classes.optionsTextSelected]}>
                No {title?.toLowerCase()} available
              </Text>
            )}

            {filteredOptions.map((option) => {
              const optionId = option[idField];
              const displayValue = option[displayField];
              const safeTempSelected = Array.isArray(tempSelected)
                ? tempSelected
                : [];
              const isSelected = safeTempSelected.includes(optionId);

              return (
                <PressableContainer
                  key={optionId}
                  style={classes.option}
                  onPress={() => handleToggleOption(optionId)}
                >
                  <View
                    style={[
                      classes.checkbox,
                      isSelected && classes.checkboxChecked,
                    ]}
                  >
                    {isSelected && (
                      <Icon name="check" size={14} style={classes.checkIcon} />
                    )}
                  </View>
                  <Text
                    style={[
                      classes.optionText,
                      isSelected && classes.optionTextSelected,
                    ]}
                  >
                    {displayValue || `No ${displayField} field`}
                  </Text>
                </PressableContainer>
              );
            })}
          </ScrollView>

          <View style={classes.buttonContainer}>
            {showClearButton && (
              <Pressable
                style={[
                  classes.button,
                  {
                    backgroundColor: theme.color.white,
                    borderWidth: 1,
                    borderColor: theme.color.primary,
                  },
                ]}
                onPress={() => {
                  if (onClear) {
                    onClear();
                    onClose();
                  }
                }}
              >
                <Text
                  style={[classes.buttonText, { color: theme.color.primary }]}
                >
                  Clear & Close
                </Text>
              </Pressable>
            )}
            <Pressable
              style={[classes.button, classes.cancelButton]}
              onPress={handleCancel}
            >
              <Text style={[classes.buttonText, classes.cancelButtonText]}>
                Cancel
              </Text>
            </Pressable>
            <Pressable
              style={[classes.button, classes.saveButton]}
              onPress={handleSave}
            >
              <Text style={[classes.buttonText, classes.saveButtonText]}>
                Save
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
